import type { CaptureItem } from './item'

/**
 * S4 (OPS-5): where the iPad keeps what it captured. Two implementations of
 * one contract (test/capture-store.test.ts): IndexedDB in the browser, and
 * memory for the tests and for a browser that refuses IndexedDB.
 *
 * Every change is one call, so in IndexedDB one transaction, whose callback
 * runs with nothing awaited inside it (WebKit commits a transaction as soon
 * as the event loop turns with no request pending).
 */
export interface CaptureChange {
  put?: readonly CaptureItem[]
  remove?: readonly string[]
}

export interface CaptureStore {
  /** Every item, in no particular order. */
  all(): Promise<CaptureItem[]>
  /**
   * Reads every item and writes what `change` returns, in one transaction:
   * nothing else changes the items in between. `change` must not await.
   * Throws QuotaExceededError when the iPad has no room left.
   */
  mutate(change: (items: readonly CaptureItem[]) => CaptureChange): Promise<void>
  /** Everything gone: a revoked device keeps nothing (A1). */
  clear(): Promise<void>
}

/** Memory only: the tests, and a browser that refuses IndexedDB (gone at the next reload). */
export function memoryStore(): CaptureStore {
  const items = new Map<string, CaptureItem>()
  // A copy each way, as IndexedDB does: no caller holds the stored object.
  const copy = (item: CaptureItem): CaptureItem => structuredClone(item)
  return {
    async all() {
      return [...items.values()].map(copy)
    },
    async mutate(change) {
      const { put = [], remove = [] } = change([...items.values()].map(copy))
      for (const item of put) items.set(item.id, copy(item))
      for (const id of remove) items.delete(id)
    },
    async clear() {
      items.clear()
    },
  }
}

const DATABASE = 'rental-staff-capture'
// Version 2: photos are kept as bytes, not a Blob (item.ts). Any item written
// by version 1 held a Blob WebKit could not store anyway, so the store is reset.
const VERSION = 2
const ITEMS = 'items'

/** Errors after which the connection is opened again once (WebKit drops it in the background). */
function connectionLost(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false
  if (error.name === 'InvalidStateError') return true
  return error.name === 'UnknownError' && /connection/i.test(error.message)
}

function settle<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function upgrade(database: IDBDatabase, from: number) {
  // Version 1: one store of items by id.
  if (from < 1) database.createObjectStore(ITEMS, { keyPath: 'id' })
  // Version 2: drop any version-1 items (Blob-shaped, never stored under WebKit).
  if (from >= 1 && from < 2) {
    database.deleteObjectStore(ITEMS)
    database.createObjectStore(ITEMS, { keyPath: 'id' })
  }
}

function open(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(DATABASE, VERSION)
    request.onupgradeneeded = (event) => upgrade(request.result, event.oldVersion)
    request.onsuccess = () => {
      const database = request.result
      // Another tab with a newer app needs to upgrade: let it, and reopen next time.
      database.onversionchange = () => database.close()
      resolve(database)
    }
    request.onerror = () => reject(request.error)
    request.onblocked = () => reject(new DOMException('capture store blocked', 'InvalidStateError'))
  })
}

/** IndexedDB, in the browser. Items are plain structured-clonable rows (photos as bytes). */
export function indexedDbStore(factory: IDBFactory): CaptureStore {
  let connection: Promise<IDBDatabase> | null = null
  const database = () => {
    connection ??= open(factory).catch((error: unknown) => {
      connection = null
      throw error
    })
    return connection
  }

  /** One transaction; on a lost connection, a new one and a second try. */
  async function run<T>(
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore, transaction: IDBTransaction) => Promise<T>,
  ): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      try {
        const db = await database()
        // Strict: a photo written is on disk before the screen says it is kept.
        const transaction = db.transaction(ITEMS, mode, {
          durability: 'strict',
        })
        // A failed request aborts the transaction: its error (a full disk) is the abort's.
        const done = new Promise<void>((resolve, reject) => {
          transaction.oncomplete = () => resolve()
          transaction.onabort = () =>
            reject(transaction.error ?? new DOMException('aborted', 'AbortError'))
        })
        const [result] = await Promise.all([
          work(transaction.objectStore(ITEMS), transaction),
          done,
        ])
        return result
      } catch (error) {
        if (attempt > 0 || !connectionLost(error)) throw error
        const lost = connection
        connection = null
        void lost?.then((db) => db.close()).catch(() => {})
      }
    }
  }

  return {
    all() {
      return run('readonly', (store) => settle(store.getAll() as IDBRequest<CaptureItem[]>))
    },
    mutate(change) {
      return run('readwrite', (store, transaction) => {
        const read = store.getAll() as IDBRequest<CaptureItem[]>
        return new Promise<void>((resolve, reject) => {
          read.onerror = () => reject(read.error)
          read.onsuccess = () => {
            try {
              // Inside the request's callback, nothing awaited: the transaction stays open.
              const { put = [], remove = [] } = change(read.result)
              for (const item of put) store.put(item)
              for (const id of remove) store.delete(id)
              resolve()
            } catch (error) {
              transaction.abort()
              reject(error)
            }
          }
        })
      })
    },
    clear() {
      return run('readwrite', (store) => settle(store.clear()).then(() => undefined))
    },
  }
}

/** The store for this browser: IndexedDB when it opens, memory when it is refused. */
export async function openCaptureStore(): Promise<{
  store: CaptureStore
  durable: boolean
}> {
  if (typeof indexedDB === 'undefined') return { store: memoryStore(), durable: false }
  const store = indexedDbStore(indexedDB)
  try {
    await store.all()
    return { store, durable: true }
  } catch {
    // A private window that refuses IndexedDB: capture still works until a reload.
    return { store: memoryStore(), durable: false }
  }
}
